import './styles.scss'

import { InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import Analytics, {
  EventProperties,
  Events,
  UserProperties
} from 'analytics/Analytics'
import { CheckmarkIcon } from 'assets/icons'
import Button from 'components/buttons/Button'
import GenericDialog from 'components/dialogs/NoInternetDialog'
import { analyticsFeatureToTierFeature } from 'components/tier/OutOfTierDialog'
import { tierToPlan } from 'components/tier/TierHover'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import updateLocale from 'dayjs/plugin/updateLocale'
import { t } from 'i18next'
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  changePlanAndUpdateAccount,
  pauseSubscriptionAndUpdateAccount,
  tryPlanAndUpdateAccount,
  updateAccountAfterPlanSubscription
} from 'services/api/payments'
import { Account } from 'services/db/Queries/AccountQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { AccountDoc, AccountProduct, PaddleCheckout } from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  GROUP_CAL_PROD_PLANIDS,
  hasExhaustedTrialOptions,
  highestActiveProduct,
  highestActiveTier,
  isActivePlanFromAnotherStore,
  P_SKU_BUSINESS_MONTHLY,
  P_SKU_BUSINESS_PLUS_MONTHLY,
  P_SKU_BUSINESS_PLUS_YEARLY,
  P_SKU_BUSINESS_YEARLY,
  P_SKU_PRO_MONTHLY,
  P_SKU_PRO_MONTHLY_NO_TRIAL,
  P_SKU_PRO_YEARLY,
  PADDLE_SKU_BUSINESS_MONTHLY,
  PADDLE_SKU_BUSINESS_PLUS_MONTHLY,
  PADDLE_SKU_BUSINESS_PLUS_YEARLY,
  PADDLE_SKU_BUSINESS_YEARLY,
  PADDLE_SKU_ENTERPRISE_MONTHLY,
  PADDLE_SKU_ENTERPRISE_YEARLY,
  PADDLE_SKU_PRO_MONTHLY,
  PADDLE_SKU_PRO_YEARLY,
  paddlePlanToTier,
  paddleProductToTier,
  Tier,
  trialProduct
} from 'utils/api/accounts'
import { TierFeatures } from 'utils/api/group_state'
import { syncLocalCalendarsToServer } from 'utils/api/sync_local_calendars_to_server/sync_local_calendars_to_server'
import {
  getAdminsCountByPlanKey,
  getPhotoQuotaLimitByPlanKey
} from 'utils/api/tiers'
import { isGroupcalApp } from 'utils/appType'
import { APP_COLOR, hexToRgba } from 'utils/colors'
import { showPurchaseConfetti } from 'utils/confetti'
import { isTestEnv } from 'utils/env'

import ConfirmationDialog from '../ConfirmationDialog'
import BillingToggle from './BillingToggle'
import { PaddlePrice } from './model'
import PricingTooltip from './PricingTooltip'

dayjs.extend(relativeTime)
dayjs.extend(updateLocale)

dayjs.updateLocale('en', {
  relativeTime: {
    future: '%s',
    past: '%s ago',
    s: 'a few seconds',
    m: '1 minute',
    mm: '%d minutes',
    h: '1 hour',
    hh: '%d hours',
    d: '1 day',
    dd: '%d days',
    M: '1 month',
    MM: '%d months',
    y: '1 year',
    yy: '%d years'
  }
})

export type PlanName = '_free_tier' | 'tier1' | 'tier2' | 'tier3' | 'tier4'

interface PlanInfoProps {
  setOpen: (b: boolean) => void
  account?: Model<AccountDoc>
  recommendedPlan: PlanName | null | undefined
  onPurchaseDialogClose?: (
    userAction: 'PURCHASED' | 'DIALOG_CLOSED' | 'PURCHASE_ABORTED',
    plan: PlanName
  ) => void
  open: boolean
  features?: string[] | null
}

type Plan = {
  name: string
  key: PlanName
  description: string
  annualPlanId?: number
  monthlyPlanId?: number
  monthlyPrice: string
  annualPrice: string
  annualTotalPrice?: string
}

const PLAN_ORDER: PlanName[] = [
  '_free_tier',
  'tier1',
  'tier2',
  'tier3',
  'tier4'
]

const PLAN_EYEBROW_KEY: Partial<Record<PlanName, string>> = {
  tier1: 'planEyebrowPro',
  tier2: 'planEyebrowTeam',
  tier3: 'planEyebrowOrg',
  tier4: 'planEyebrowEnterprise'
}

type PlanCardFeature = {
  translationKey: string
  featureKey: string
  valueKey?: 'eventPhotoAttachments' | 'numAdmins'
}

const PLAN_CARD_FEATURES: Partial<Record<PlanName, PlanCardFeature[]>> = {
  tier1: [
    {
      translationKey: 'planFeaturePhotoAttachments',
      featureKey: 'eventPhotoAttachments',
      valueKey: 'eventPhotoAttachments'
    },
    {
      translationKey: 'planFeatureCalendarPermissions',
      featureKey: 'permManagementAddEvents'
    },
    { translationKey: 'planFeatureMemberColor', featureKey: 'memberColors' },
    {
      translationKey: 'planFeaturePersonalCalendars',
      featureKey: 'sharePersonalCalendar'
    }
  ],
  tier2: [
    {
      translationKey: 'planFeatureCalendarAdmins',
      featureKey: 'numAdmins',
      valueKey: 'numAdmins'
    },
    {
      translationKey: 'planFeaturePhotoAttachments',
      featureKey: 'eventPhotoAttachments',
      valueKey: 'eventPhotoAttachments'
    },
    {
      translationKey: 'planFeatureEventOwnership',
      featureKey: 'assignEventOwnership'
    },
    {
      translationKey: 'planFeatureAdvancedPermissions',
      featureKey: 'seeEventsDetails'
    }
  ],
  tier3: [
    {
      translationKey: 'planFeatureUnlimitedCalendarAdmins',
      featureKey: 'numAdmins'
    },
    {
      translationKey: 'planFeaturePhotoAttachments',
      featureKey: 'eventPhotoAttachments',
      valueKey: 'eventPhotoAttachments'
    },
    {
      translationKey: 'planFeaturePermissionsPrivacy',
      featureKey: 'seeEventsDetails'
    },
    { translationKey: 'calendarChannels', featureKey: 'calendarChannels' },
    {
      translationKey: 'planFeatureLinkCalendarsSync',
      featureKey: 'syncEvents'
    }
  ],
  tier4: [
    {
      translationKey: 'planFeatureUnlimitedAttachments',
      featureKey: 'eventPhotoAttachments'
    },
    {
      translationKey: 'planFeatureSecurityGovernance',
      featureKey: 'customLegalAndSecurityReview'
    },
    {
      translationKey: 'planFeatureRestApiWebhooks',
      featureKey: 'advancedRestApi'
    },
    {
      translationKey: 'planFeatureWhiteLabeledApps',
      featureKey: 'whiteLabeledMobileApp'
    }
  ]
}

export const supportedFeatures: Array<{
  title: string
  key: string
  desc: string
  category: string
  linkText?: string
  linkUrl?: string
  imageUrl?: string
  _free_tier: string | boolean
  tier1: string | boolean
  tier2: string | boolean
  tier3: string | boolean
  tier4: string | boolean
  outOfTierExplanation?: string
  [key: string]: string | boolean | undefined
}> = [
  {
    title: t('numberOfCalendars'),
    key: 'numCalendars',
    category: t('planCategoryCoreCapacity'),
    _free_tier: `${t('unlimited')}`,
    tier1: `${t('unlimited')}`,
    tier2: `${t('unlimited')}`,
    tier3: `${t('unlimited')}`,
    tier4: `${t('unlimited')}`,
    desc: t('num_calendars_desc')
  },
  {
    title: t('numberOfEvents'),
    key: 'numEvents',
    category: t('planCategoryCoreCapacity'),
    _free_tier: `${t('unlimited')}`,
    tier1: `${t('unlimited')}`,
    tier2: `${t('unlimited')}`,
    tier3: `${t('unlimited')}`,
    tier4: `${t('unlimited')}`,
    desc: t('num_events_desc')
  },
  {
    title: t('eventPhotoAttachments'),
    key: 'eventPhotoAttachments',
    category: t('planCategoryCoreCapacity'),
    _free_tier: '25',
    tier1: '150',
    tier2: '500',
    tier3: '1500',
    tier4: `${t('unlimited')}`,
    desc: t('eventPhotoAttachmentsTooltip'),
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/Event_photo_attachment.jpg'
  },
  {
    title: t('numberOfAdmins'),
    key: 'numAdmins',
    category: t('planCategoryCoreCapacity'),
    _free_tier: '1',
    tier1: '1',
    tier2: '3',
    tier3: `${t('unlimited')}`,
    tier4: `${t('unlimited')}`,
    desc: t('num_admins_desc'),
    linkText: t('calendarsMembersRoles'),
    linkUrl:
      'https://www.groupcal.app/powerful-permissions-control/#calendar-members-roles',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/calendar_members_roles.jpg?ver=1774857138'
  },
  {
    title: t('landlineNumber'),
    key: 'landline',
    category: t('planCategoryCoreCapacity'),
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('landline_feature_desc')
  },
  {
    title: t('chooseAddEvents'),
    key: 'permManagementAddEvents',
    category: t('planCategoryPermissions'),
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('add_events_desc'),
    linkText: t('whoCanAddOrModifyEvents'),
    linkUrl:
      'https://www.groupcal.app/powerful-permissions-control/#control-event-editing',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/permission_add_edit_events.jpg'
  },
  {
    title: t('chooseCalendarNamePhoto'),
    key: 'permManagementCalendarName',
    category: t('planCategoryPermissions'),
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('change_calendar_desc')
  },
  {
    title: t('chooseCanAddMembers'),
    key: 'permManagementAddMembers',
    category: t('planCategoryPermissions'),
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('add_members_desc')
  },
  {
    title: t('whoCanSeeEventsDetails'),
    key: 'seeEventsDetails',
    category: t('planCategoryPermissions'),
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('eventVisibilityDesc'),
    outOfTierExplanation: t('eventVisibilityOutOfTier'),
    linkText: t('eventVisibility'),
    linkUrl:
      'https://www.groupcal.app/powerful-permissions-control/#set-event-detail-visibility',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/permission_view_free_busy.jpg'
  },
  {
    title: t('assignEventOwnership'),
    key: 'assignEventOwnership',
    category: t('planCategoryPermissions'),
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('event_ownership_desc'),
    linkText: t('eventOwnership'),
    linkUrl:
      'https://www.groupcal.app/features-seamless-sharing-collaboration/#event-ownership',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/assign_event_ownership.jpg'
  },
  {
    title: t('membersCanEditTheirEventsOnly'),
    key: 'membersCanEditTheirEventsOnly',
    category: t('planCategoryPermissions'),
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('membersCanEditTheirEventsOnlyDesc'),
    linkText: t('editingOnlyOwnEvents'),
    linkUrl:
      'https://www.groupcal.app/powerful-permissions-control/#edit_own_events_only',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/permission_edit_own_events.jpg'
  },
  {
    title: t('rsvpForEvents'),
    key: 'rsvp',
    category: t('planCategorySharing'),
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('rsvp_desc')
  },
  {
    title: t('planFeatureMemberColor'),
    key: 'memberColors',
    category: t('planCategorySharing'),
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('color_desc'),
    linkText: t('photosAndColorCoding'),
    linkUrl:
      'https://www.groupcal.app/features-seamless-sharing-collaboration/#member-colors',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/members_color_photos.jpg'
  },
  {
    title: t('planFeaturePersonalCalendars'),
    key: 'sharePersonalCalendar',
    category: t('planCategorySharing'),
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('purchaseCalendarShareDesc'),
    linkText: t('shareYourPersonalCalendars'),
    linkUrl:
      'https://www.groupcal.app/features-seamless-sharing-collaboration/#share_personal_calendars',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/persoal_accounts_integration.jpg'
  },
  {
    title: t('calendarChannels'),
    key: 'calendarChannels',
    category: t('planCategoryArchitecture'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('channels_desc'),
    linkText: t('calendarChannels'),
    linkUrl:
      'https://www.groupcal.app/features-brand-publish-embed/#public-calendar-channels',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/shearchable_public_calendars.jpg'
  },
  {
    title: t('syncEventsAcrossCalendars'),
    key: 'syncEvents',
    category: t('planCategoryArchitecture'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('sync_desc'),
    linkText: t('masterSchedules'),
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/sync_events_across_calendars.jpg',
    linkUrl:
      'https://www.groupcal.app/features-centralized-calendar-management/#sync_events_across_calendars'
  },
  {
    title: t('linkedCalendars'),
    key: 'linkCalendars',
    category: t('planCategoryArchitecture'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('linkOtherCalendarToThisOneDesc')
  },
  {
    title: t('hideGroupcalBrand'),
    key: 'removeBranding',
    category: t('planCategoryPublishing'),
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('hideGroupcalBrandDesc')
  },
  {
    title: t('businessPage'),
    key: 'business',
    category: t('planCategoryPublishing'),
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('business_page_desc')
  },
  {
    title: t('verifiedCalendars'),
    key: 'verifiedChannels',
    category: t('planCategoryPublishing'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('verified_desc'),
    linkText: t('calendarVerificationTitle'),
    linkUrl:
      'https://www.groupcal.app/features-brand-publish-embed/#calendar-verification-badge',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/calendar_verification.jpg'
  },
  {
    title: t('embedMultipleCalendars'),
    key: 'embedCalendars',
    category: t('planCategoryPublishing'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('embedMultipleCalendarsDesc')
  },
  {
    title: t('communityView'),
    key: 'communityView',
    category: t('planCategoryPublishing'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('communityViewDesc'),
    linkText: t('embedingCalendars'),
    imageUrl:
      'https://www.groupcal.app/wp-content/uploads/2026/03/Cards-view-on-a-website.jpg',
    linkUrl:
      'https://www.groupcal.app/features-brand-publish-embed/#embed-on-your-website'
  },
  {
    title: t('prioritySupport'),
    key: 'prioritySupport',
    category: t('planCategorySupport'),
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('support_desc')
  },
  {
    title: t('advancedRestApiTitle'),
    key: 'advancedRestApi',
    category: t('planCategorySupport'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('advancedRestApiDesc')
  },
  {
    title: t('realTimeWebhooksTitle'),
    key: 'realTimeWebhooks',
    category: t('planCategorySupport'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('realTimeWebhooksDesc')
  },
  {
    title: t('whiteLabeledWebViewTitle'),
    key: 'whiteLabeledWebView',
    category: t('planCategorySupport'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('whiteLabeledWebViewDesc')
  },
  {
    title: t('whiteLabeledMobileAppTitle'),
    key: 'whiteLabeledMobileApp',
    category: t('planCategorySupport'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('whiteLabeledMobileAppDesc')
  },
  {
    title: t('customLegalAndSecurityReviewTitle'),
    key: 'customLegalAndSecurityReview',
    category: t('planCategorySupport'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('customLegalAndSecurityReviewDesc')
  },
  {
    title: t('dedicatedSuccessManagerTitle'),
    key: 'dedicatedSuccessManager',
    category: t('planCategorySupport'),
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('dedicatedSuccessManagerDesc')
  }
]

export function logPurchasesAnalytics(
  event: Events,
  account?: AccountDoc,
  additionalData?: {},
  recommendedPlan?: PlanName | null,
  features?: string[] | null
) {
  if (account) {
    const product = highestActiveProduct(account)
    const data = {
      [UserProperties.IS_ACTIVE_SUBS]: product != null ? 'Yes' : 'No',
      [UserProperties.ACTIVE_SUBS]: product?.ID,
      [EventProperties.NEEDED_TIER]:
        recommendedPlanToAnalyticsTier(recommendedPlan),
      [EventProperties.FEATURES_OPENED_WITH]: features?.join(', '),
      [UserProperties.IS_TRIAL_EXPIRED]: hasExhaustedTrialOptions(account)
        ? 'Yes'
        : 'No',
      [UserProperties.IS_IN_ACTIVE_TRIAL]:
        trialProduct(account) != undefined && product?.Status === 'trialing'
          ? 'Yes'
          : 'No',
      [EventProperties.TRIAL_OR_PLAN_EXPIRES_IN]:
        product?.Status === 'trialing' || product?.Status === 'cancelled'
          ? dayjs(Number(product?.ExpiryDate ?? '0') * 1000).diff(
              dayjs(),
              'seconds'
            )
          : undefined,
      ...additionalData
    }
    if (product?.Status !== 'trialing' && product?.Status !== 'cancelled') {
      delete data[EventProperties.TRIAL_OR_PLAN_EXPIRES_IN]
    }
    Analytics.getInstance().sendEvent(event, data)
  }
}

export default function PlanInfo(props: PlanInfoProps) {
  const muiTheme = useTheme()
  const [subscriptionType, setSubscriptionType] = useState<
    'monthly' | 'annually'
  >(highestActiveProduct(props.account) ? 'monthly' : 'annually')
  const [userSubscriptionType, setUserSubscriptionType] = useState<
    'monthly' | 'annually'
  >(highestActiveProduct(props.account) ? 'monthly' : 'annually')

  Logger.orange('PlanInfo:HighestProduct', highestActiveProduct(props.account))

  useEffect(() => {
    // set current subscription type based on current user plan
    if (props.account) {
      const activePlan = highestActiveProduct(props.account)
      if (activePlan?.ID?.includes('annually')) {
        setSubscriptionType('annually')
        setUserSubscriptionType('annually')
      } else if (activePlan) {
        setSubscriptionType('monthly')
        setUserSubscriptionType('monthly')
      } else {
        setSubscriptionType('annually')
        setUserSubscriptionType('annually')
      }
    } else {
      setSubscriptionType('annually')
      setUserSubscriptionType('annually')
    }
  }, [props.account, props.open])

  const isSameSubscriptionType = subscriptionType === userSubscriptionType

  const openedForTierFeatures = props.features?.map((feature) =>
    analyticsFeatureToTierFeature(feature)
  )
  Logger.blue('PlansInfo:Features', props.features)
  Logger.blue('PlansInfo:OpenedForTierFeatures', openedForTierFeatures)

  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [infoDialogShowing, setInfoDialogShowing] = useState<boolean>(false)
  const [activeMessage, setActiveMessage] = useState<string>('')

  const {
    setOpen,
    account,
    open,
    recommendedPlan,
    onPurchaseDialogClose,
    features
  } = props
  const highlightAccent =
    muiTheme.palette.mode === 'dark' ? '#ff0032' : APP_COLOR

  useEffect(() => {
    if (open) {
      //log analytics only on opening
      logPurchasesAnalytics(
        Events.OPEN_INAPP_PURCHASE,
        props.account,
        {},
        recommendedPlan,
        features
      )
    }
  }, [open])

  const activePlan: AccountProduct | null | undefined =
    account && highestActiveProduct(account)

  const toggleSubscriptionType = (e: { preventDefault: () => void }) => {
    e.preventDefault()
    if (subscriptionType === 'monthly') {
      setSubscriptionType('annually')
    } else {
      setSubscriptionType('monthly')
    }
  }

  const getPlanFeatures = (planKey: string) => {
    return supportedFeatures.map((feat) => ({
      key: feat.key,
      title: feat.title,
      value: feat[planKey]
    }))
  }

  const syncLocalCalendarsIfNeeded = (acc?: AccountDoc) => {
    // in case of account changes (products) we trigger calendar sync as we might received a product
    if (highestActiveTier(acc) >= Tier.PRO) {
      syncLocalCalendarsToServer()
    }
  }

  const shouldShowPurchaseConfetti = (previousTier: Tier, nextTier: Tier) => {
    return nextTier >= previousTier
  }

  const handleTryForFree = (plan: Plan) => {
    logPurchasesAnalytics(
      Events.CLICK_INAPP_PURCHASE,
      props.account,
      {
        [EventProperties.IS_PURCHASING_TRIAL]: 'Yes'
      },
      recommendedPlan,
      features
    )
    const planIdkey = [plan.key, subscriptionType].join('_')

    const planId = GROUP_CAL_PROD_PLANIDS.find((i) => i.includes(planIdkey))
    if (!planId) {
      throw `${planIdkey} Plan not supported. List ${GROUP_CAL_PROD_PLANIDS}`
    }

    const previousTier = highestActiveTier(account)
    const shouldCelebratePurchase = shouldShowPurchaseConfetti(
      previousTier,
      paddlePlanToTier(plan.key)
    )

    setIsProcessing(true)
    account &&
      tryPlanAndUpdateAccount(account, planId)
        .then((updatedAccount) => {
          logPurchasesAnalytics(
            Events.INAPP_PURCHASED,
            updatedAccount,
            {
              [EventProperties.IS_PURCHASING_TRIAL]: 'Yes'
            },
            recommendedPlan,
            features
          )

          if (shouldCelebratePurchase) {
            showPurchaseConfetti()
          }

          logGTag('Start Trial', {
            send_to: 'AW-10788534611/I-CICPvNnY0YENPyr5go',
            value: '0',
            currency: 'ILS',
            transaction_id: ''
          })

          syncLocalCalendarsIfNeeded(updatedAccount)
          typeof onPurchaseDialogClose === 'function' &&
            updatedAccount &&
            onPurchaseDialogClose('PURCHASED', plan.key)
        })
        .finally(() => setIsProcessing(false))
  }

  const handlePurchase = (plan: Plan) => {
    logPurchasesAnalytics(
      Events.CLICK_INAPP_PURCHASE,
      props.account,
      {
        [EventProperties.IS_PURCHASING_TRIAL]: 'No'
      },
      recommendedPlan,
      features
    )
    const planId =
      subscriptionType === 'monthly' ? plan.monthlyPlanId : plan.annualPlanId

    const previousTier = highestActiveTier(account)
    const shouldCelebratePurchase =
      planId !== undefined &&
      shouldShowPurchaseConfetti(previousTier, paddleProductToTier(planId))

    const price =
      subscriptionType === 'monthly' ? plan.monthlyPrice : plan.annualPrice
    if (planId) {
      window?.Paddle?.Checkout.open({
        product: planId,
        title: t('groupcalPlan', { planName: plan.name }),
        email: account?.PaymentEmail || account?.Email,
        method: 'overlay',
        displayModeTheme: 'light',
        passthrough: JSON.stringify({
          userId: account?._id,
          amplitude_deviceid: Analytics.getInstance().amplitudeDeviceId,
          singularid: Analytics.getInstance().singularDeviceId,
          custom_id: account?._id
        }),
        closeCallback: (e: any) => {
          typeof onPurchaseDialogClose === 'function' &&
            account &&
            onPurchaseDialogClose(
              'PURCHASE_ABORTED',
              activePlan?.ID
                ? subscriptionIdToPlan(activePlan?.ID?.toLowerCase())
                : '_free_tier'
            )
        },
        successCallback: (e: PaddleCheckout) => {
          setIsProcessing(true)
          window?.Paddle?.Order?.details(
            e.checkout.id,
            (data: { state: string; order: { subscription_id: any } }) => {
              if (account) {
                if (data.state === 'processed') {
                  updateAccountAfterPlanSubscription(account, {
                    ...e,
                    checkout: {
                      ...e.checkout,
                      subscriptionId: data.order.subscription_id
                    }
                  })
                    .then((updatedAccount) => {
                      logPurchasesAnalytics(
                        Events.INAPP_PURCHASED,
                        updatedAccount,
                        {
                          [EventProperties.IS_PURCHASING_TRIAL]: 'No'
                        },
                        recommendedPlan,
                        features
                      )

                      if (shouldCelebratePurchase) {
                        showPurchaseConfetti()
                      }

                      logGTag('Purchase', {
                        send_to: 'AW-10788534611/9hGiCP7NnY0YENPyr5go',
                        value: price.slice(1, price.length),
                        currency: price.slice(0, 1),
                        transaction_id: e.checkout.id
                      })

                      syncLocalCalendarsIfNeeded(updatedAccount)

                      typeof onPurchaseDialogClose === 'function' &&
                        updatedAccount &&
                        onPurchaseDialogClose(
                          'PURCHASED',
                          subscriptionIdToPlan(e.product.id)
                        )
                    })
                    .finally(() => setIsProcessing(false))
                }
              }
            },
            false
          )
        }
      })
    }
  }

  const handleCancelPlan = (plan: Plan) => {
    logPurchasesAnalytics(
      Events.INAPP_CANCELLED,
      account,
      {},
      recommendedPlan,
      features
    )

    if (account && isActivePlanFromAnotherStore(account)) {
      setInfoDialogShowing(true)
      setActiveMessage(`${t('useTheAppToCancel')}`)
    } else if (account && activePlan && activePlan.SubscriptionId) {
      setIsProcessing(true)
      pauseSubscriptionAndUpdateAccount(
        account,
        activePlan?.SubscriptionId
      ).finally(() => setIsProcessing(false))
    }
  }

  const handleEnterpriseContact = () => {
    window.open('https://www.groupcal.app/get-a-demo/', '_blank')
  }

  const handleActiveMessageClose = () => {
    setInfoDialogShowing(false)
    setActiveMessage('')
  }

  const handleChangePlan = (plan: Plan) => {
    logPurchasesAnalytics(
      Events.CLICK_INAPP_PURCHASE,
      props.account,
      {
        [EventProperties.IS_PURCHASING_TRIAL]: 'No'
      },
      recommendedPlan,
      features
    )
    if (account && isActivePlanFromAnotherStore(account)) {
      setInfoDialogShowing(true)
      setActiveMessage(
        `${t('yourActivePlanPurchased', { store: activePlan?.Store })}`
      )
    } else if (activePlan?.Status === 'trialing') {
      setInfoDialogShowing(true)
    } else if (
      activePlan &&
      activePlan.SubscriptionId &&
      plan.annualPlanId &&
      plan.monthlyPlanId
    ) {
      const previousTier = highestActiveTier(account)
      const shouldCelebratePurchase = shouldShowPurchaseConfetti(
        previousTier,
        paddlePlanToTier(plan.key)
      )

      setIsProcessing(true)
      account &&
        changePlanAndUpdateAccount(
          account,
          activePlan.SubscriptionId,
          subscriptionType === 'monthly'
            ? plan?.monthlyPlanId
            : plan?.annualPlanId
        )
          .then((updatedAccount) => {
            logPurchasesAnalytics(
              Events.INAPP_PURCHASED,
              updatedAccount,
              {
                [EventProperties.IS_PURCHASING_TRIAL]: 'No'
              },
              recommendedPlan,
              features
            )

            if (shouldCelebratePurchase) {
              showPurchaseConfetti()
            }

            setUserSubscriptionType(subscriptionType)
            account &&
              typeof onPurchaseDialogClose === 'function' &&
              onPurchaseDialogClose(
                'DIALOG_CLOSED',
                plan.annualPlanId
                  ? subscriptionIdToPlan(plan.annualPlanId)
                  : '_free_tier'
              )
          })
          .finally(() => {
            setIsProcessing(false)
          })
    }
  }

  const handleDialogClose = () => {
    setTimeout(() => {
      LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
    }, 250) // timeout to wait close animation
    Account()
      .then((acc) => {
        acc &&
          typeof onPurchaseDialogClose === 'function' &&
          onPurchaseDialogClose(
            'DIALOG_CLOSED',
            tierToPlan(highestActiveTier(acc))
          )
      })
      .finally(() => {
        setOpen(false)
      })
  }

  const PlanNotice = (props: { plan: Plan }) => {
    const { plan } = props
    const isActivePlan = activePlan?.ID?.includes(plan.key)
    const isRecommendedPlan =
      recommendedPlan?.includes(plan.key) &&
      !activePlan?.ID?.includes(recommendedPlan)
    if (isActivePlan) {
      const date = dayjs(Number(activePlan?.ExpiryDate) * 1000).fromNow()
      let notice = t('existingPlan')
      if (activePlan?.Status === 'trialing') notice = t('freeTrialEndsIn')
      if (activePlan?.Status === 'cancelled') notice = t('existingPlanEndsIn')

      return (
        <span
          className={`plan-notice ${plan.key} existing-plan ${
            activePlan?.Status == 'active' ? 'active' : ''
          }`}
        >
          {notice}
          {(activePlan?.Status === 'trialing' ||
            activePlan?.Status === 'cancelled') && (
            <span className="plan-relative-date">{date}</span>
          )}
        </span>
      )
    }

    if (!activePlan && plan.key === '_free_tier') {
      return (
        <span className={`plan-notice ${plan.key} active existing-plan`}>{`${t(
          'existingPlan'
        )}`}</span>
      )
    }

    if (isRecommendedPlan) {
      return (
        <span className={`plan-notice ${plan.key} recommended`}>
          {t('requiredPlan')}
        </span>
      )
    }
    return null
  }

  const PlanActionBtn = (props: { plan: Plan; inTable?: boolean }) => {
    const { plan, inTable } = props
    let action = null
    let label = ''
    let btnClass = ''

    const canTry = (plan.monthlyPlanId || plan.annualPlanId) && !activePlan

    const isNotActivePlanAndOnTrial =
      (plan.monthlyPlanId || plan.annualPlanId) &&
      activePlan &&
      activePlan.Status === 'trialing' &&
      !activePlan?.ID?.includes(plan.key)

    const canPurchase =
      activePlan &&
      activePlan.Status === 'trialing' &&
      activePlan?.ID?.includes(plan.key)

    const canChangePlan =
      ((plan.monthlyPlanId || plan.annualPlanId) &&
        activePlan &&
        activePlan.Status !== 'trialing' &&
        !activePlan?.ID?.includes(plan.key)) ||
      (activePlan &&
        activePlan.Status !== 'trialing' &&
        activePlan.Status !== 'cancelled' &&
        activePlan?.ID?.includes(plan.key) &&
        !isSameSubscriptionType)

    const canCancel =
      activePlan &&
      activePlan.Status !== 'trialing' &&
      activePlan.Status !== 'cancelled' &&
      activePlan?.ID?.includes(plan.key) &&
      isSameSubscriptionType

    const canActivate =
      activePlan &&
      activePlan.Status !== 'trialing' &&
      activePlan.Status === 'cancelled' &&
      activePlan?.ID?.includes(plan.key)

    const cantTry = hasExhaustedTrialOptions(account)

    if (canTry || isNotActivePlanAndOnTrial) {
      action = handleTryForFree.bind(null, plan)
      label = t('tryForFree')
      btnClass = isNotActivePlanAndOnTrial ? 'changetrialplan-btn' : 'trial-btn'
    }

    if (canPurchase) {
      action = handlePurchase.bind(null, plan)
      label = t('purchase')
      btnClass = 'purchase-btn'
    }

    if (cantTry) {
      action = handlePurchase.bind(null, plan)
      label = t('continue')
      btnClass = 'purchase-btn'
    }

    if (canCancel) {
      action = handleCancelPlan.bind(null, plan)
      label = t('cancelPlan')
      btnClass = 'cancel-plan-btn'
    }

    if (canChangePlan || canActivate) {
      action = handleChangePlan.bind(null, plan)
      const activePlanMeta = plans.find((p) => activePlan?.ID.includes(p?.key))

      function isPlanHigher(plan1: Plan | undefined, plan2: Plan | undefined) {
        if (!plan1 || !plan2) return false
        const tier1 = tierToLevel(plan1.key)
        const tier2 = tierToLevel(plan2.key)
        return tier1 > tier2
      }

      const isHigherPlan = activePlanMeta && isPlanHigher(plan, activePlanMeta)
      if (activePlan.Status === 'cancelled') {
        label = t('activate')
      } else if (isHigherPlan) {
        label = t('upgrade')
      } else if (
        tierToLevel(plan.key) === tierToLevel(activePlanMeta?.key ?? '') &&
        !isSameSubscriptionType
      ) {
        label = t('changePlan')
      } else {
        label = t('downgrade')
      }
      btnClass = 'changeplan-btn'
    }

    if (plan.key === '_free_tier') {
      return null
    }

    const tableCtaClass = inTable ? 'plan-table-cta' : ''

    if (plan.key === 'tier4') {
      return (
        <Button
          id="purchase-tier4"
          className={`trial-btn ${tableCtaClass}`.trim()}
          onClick={handleEnterpriseContact}
          textColor="#fff"
        >
          {t('contactSales')}
        </Button>
      )
    }

    if (!action) {
      return null
    }

    return (
      <Button
        id={`purchase-${plan.key}`}
        className={`trial-btn ${btnClass} ${tableCtaClass}`.trim()}
        onClick={action?.bind(null)}
        textColor="#fff"
      >
        {label}
      </Button>
    )
  }

  const DialogTitle = () => {
    const hasUsedPlanBefore =
      activePlan?.Status === 'active' ||
      activePlan?.Status === 'cancelled' ||
      hasExhaustedTrialOptions(account)
    const openedForFeature = Boolean(
      openedForTierFeatures && openedForTierFeatures.length > 0
    )

    if (openedForFeature) {
      return (
        <>
          <h2 className="membership-title">{t('upgradeToUseThisFeature')}</h2>
          <h3 className="membership-subtitle">
            {t(hasUsedPlanBefore ? 'cancelAnytime' : 'try7days')}
          </h3>
        </>
      )
    }

    return (
      <>
        <h2 className="membership-title membership-title--hero">
          <span className="membership-title__line">
            {t('pricingHeroTitleLine1')}
          </span>
          <span className="membership-title__highlight">
            {t('pricingHeroTitleLine2')}
          </span>
        </h2>
        <h3 className="membership-subtitle membership-subtitle--hero">
          {t('pricingHeroSubtitle')}
        </h3>
      </>
    )
  }

  const [, setTierCacheVersion] = useState(0)

  useEffect(() => {
    const handleTierCacheChange = (event: StorageEvent) => {
      if (event.key && event.key !== LocalStorageKey.TIERS_CACHE) return
      setTierCacheVersion((version) => version + 1)
    }

    window.addEventListener('storage', handleTierCacheChange)

    return () => {
      window.removeEventListener('storage', handleTierCacheChange)
    }
  }, [])

  const [pricesLoaded, setPricesLoaded] = useState(false)

  const [plans, setPlans] = useState<Array<Plan>>([
    {
      name: t('freePlanName'),
      key: '_free_tier',
      description: t('freeDesc'),
      monthlyPrice: '0',
      annualPrice: '0'
    },
    {
      name: t('tierPro'),
      key: 'tier1',
      annualPlanId: PADDLE_SKU_PRO_YEARLY,
      monthlyPlanId: PADDLE_SKU_PRO_MONTHLY,
      description: t('proDesc'),
      monthlyPrice: '',
      annualPrice: ''
    },
    {
      name: t('tierBusiness'),
      key: 'tier2',
      annualPlanId: PADDLE_SKU_BUSINESS_YEARLY,
      monthlyPlanId: PADDLE_SKU_BUSINESS_MONTHLY,
      description: t('businessDesc'),
      monthlyPrice: '',
      annualPrice: ''
    },
    {
      name: t('tierBusinessPlus'),
      key: 'tier3',
      annualPlanId: PADDLE_SKU_BUSINESS_PLUS_YEARLY,
      monthlyPlanId: PADDLE_SKU_BUSINESS_PLUS_MONTHLY,
      description: t('businessPlusDesc'),
      monthlyPrice: '',
      annualPrice: ''
    },
    {
      name: t('tierEnterprise'),
      key: 'tier4',
      description: t('enterpriseDesc'),
      annualPlanId: PADDLE_SKU_ENTERPRISE_YEARLY,
      monthlyPlanId: PADDLE_SKU_ENTERPRISE_MONTHLY,
      monthlyPrice: '',
      annualPrice: ''
    }
  ])

  const planCardsGridRef = useRef<HTMLDivElement | null>(null)
  const plansHeaderRef = useRef<HTMLTableSectionElement | null>(null)
  const [noticeSlotHeight, setNoticeSlotHeight] = useState(22)
  const [plansHeaderHeight, setPlansHeaderHeight] = useState(0)
  const [isPlansHeaderSticky, setIsPlansHeaderSticky] = useState(false)

  useLayoutEffect(() => {
    const grid = planCardsGridRef.current
    if (!grid) return

    const measuredGroups = [
      {
        selector: '.plan-card__eyebrow',
        property: '--plan-card-eyebrow-height'
      },
      {
        selector: '.plan-card__name',
        property: '--plan-card-name-height'
      },
      {
        selector: '.plan-card__desc',
        property: '--plan-card-description-height'
      },
      {
        selector: '.plan-card__price-caption',
        property: '--plan-card-price-caption-height'
      },
      {
        selector: '.plan-card__price',
        property: '--plan-card-price-height'
      }
    ]

    const measureCardText = () => {
      measuredGroups.forEach(({ property }) => {
        grid.style.removeProperty(property)
      })

      measuredGroups.forEach(({ selector, property }) => {
        const maxHeight = Math.max(
          0,
          ...Array.from(grid.querySelectorAll<HTMLElement>(selector)).map(
            (element) => Math.ceil(element.getBoundingClientRect().height)
          )
        )

        if (maxHeight > 0) {
          grid.style.setProperty(property, `${maxHeight}px`)
        }
      })
    }

    measureCardText()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureCardText)
      return () => window.removeEventListener('resize', measureCardText)
    }

    const resizeObserver = new ResizeObserver(measureCardText)
    resizeObserver.observe(grid)
    const mutationObserver = new MutationObserver(measureCardText)
    mutationObserver.observe(grid, {
      characterData: true,
      childList: true,
      subtree: true
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [open, plans])

  useLayoutEffect(() => {
    const header = plansHeaderRef.current
    if (!header) return

    const slots = Array.from(
      header.querySelectorAll<HTMLElement>('.plan-header-mini__notice-slot')
    )

    const updateNoticeSlotHeight = () => {
      const maxHeight = Math.max(
        22,
        ...slots.flatMap((slot) => {
          const notice = slot.querySelector<HTMLElement>('.plan-notice')
          return notice
            ? [Math.ceil(notice.getBoundingClientRect().height)]
            : []
        })
      )
      const headerHeight = Math.ceil(header.getBoundingClientRect().height)

      setNoticeSlotHeight((currentHeight) =>
        currentHeight === maxHeight ? currentHeight : maxHeight
      )
      setPlansHeaderHeight((currentHeight) =>
        currentHeight === headerHeight ? currentHeight : headerHeight
      )
    }

    updateNoticeSlotHeight()

    window.addEventListener('resize', updateNoticeSlotHeight)
    return () => window.removeEventListener('resize', updateNoticeSlotHeight)
  }, [activePlan?.ExpiryDate, activePlan?.ID, activePlan?.Status, open, plans])

  useLayoutEffect(() => {
    const header = plansHeaderRef.current
    const scrollContainer = header?.closest(
      '.dialog-content-box'
    ) as HTMLElement | null
    if (!header || !scrollContainer) return

    const updateStickyState = () => {
      const headerRect = header.getBoundingClientRect()
      const containerRect = scrollContainer.getBoundingClientRect()
      const isSticky =
        scrollContainer.scrollTop > 0 && headerRect.top <= containerRect.top + 1

      setIsPlansHeaderSticky((currentState) =>
        currentState === isSticky ? currentState : isSticky
      )
    }

    updateStickyState()
    scrollContainer.addEventListener('scroll', updateStickyState, {
      passive: true
    })
    window.addEventListener('resize', updateStickyState)

    return () => {
      scrollContainer.removeEventListener('scroll', updateStickyState)
      window.removeEventListener('resize', updateStickyState)
    }
  }, [
    activePlan?.ExpiryDate,
    activePlan?.ID,
    activePlan?.Status,
    noticeSlotHeight,
    open,
    plans
  ])

  const fetchAndUpdatePrices = async () => {
    const updatedPlans = await Promise.all(
      plans.map(async (plan) => {
        try {
          if (plan.annualPlanId) {
            const annualPrice = await fetchPaddlePrice(
              plan.annualPlanId.toString()
            )
            const currencySymbol = annualPrice.price.gross.replace(
              /[\d.,\s]/g,
              ''
            )
            plans[0].annualPrice = `${currencySymbol}0`
            plans[0].monthlyPrice = `${currencySymbol}0`

            const annualPriceNumber = parseFloat(
              annualPrice.price.gross.replace(/[^\d.-]/g, '')
            )
            plan.annualPrice = `${currencySymbol}${(
              annualPriceNumber / 12
            ).toFixed(2)}`
            plan.annualTotalPrice = annualPrice.price.gross
          }
          if (plan.monthlyPlanId) {
            const monthlyPrice = await fetchPaddlePrice(
              plan.monthlyPlanId.toString()
            )
            plan.monthlyPrice = monthlyPrice.price.gross
          }
        } catch (e) {
          setPlans([])
        }
        return plan
      })
    )
    setPlans(updatedPlans)
    setPricesLoaded(true)
  }

  useEffect(() => {
    setPricesLoaded(false)
    fetchAndUpdatePrices()
  }, [open])

  useEffect(() => {
    // because of async paddle, we need to reload prices if user opens this dialog immediately after reload

    setTimeout(() => {
      Logger.orange('PlansDialog:', plans)
      if (
        plans.some(
          (plan) =>
            (plan.monthlyPrice === '' || plan.annualPrice === '') &&
            (plan.monthlyPlanId || plan.annualPlanId)
        )
      ) {
        fetchAndUpdatePrices()
      }
    }, 3000)
  }, [])

  const fetchPaddlePrice = (planId: string): Promise<PaddlePrice> => {
    return new Promise((resolve, reject) => {
      if (
        window.Paddle &&
        window.Paddle.Product &&
        window.Paddle.Product.Prices
      ) {
        window.Paddle.Product.Prices(planId, function (price: PaddlePrice) {
          Logger.red('PaddleProduct', price)
          resolve(price)
        })
      } else {
        Logger.red('Paddle is not defined')
        reject(new Error('Paddle is not defined'))
      }
    })
  }

  const annualSavings = () => {
    if (!pricesLoaded) {
      return null
    }

    const proPlan = plans.find((item) => item.key === 'tier3')
    if (!proPlan) {
      return '-'
    }

    const priceMonthly = proPlan?.monthlyPrice
      .replace(',', '')
      .replace(/^\D+/g, '')
    const priceAnnually = proPlan?.annualPrice
      .replace(',', '')
      .replace(/^\D+/g, '')

    const ratio = parseFloat(priceAnnually) / parseFloat(priceMonthly)

    const savings = 1 - ratio
    return Math.round(savings * 100)
  }

  const yearlyLabel = t('yearlySave', { percent: annualSavings() })
  const [yearlyPeriodLabel, savingsLabel] = yearlyLabel.split('/')

  const getDisplayedPrice = (plan: Plan) => {
    return pricesLoaded || (plan.monthlyPrice !== '' && plan.annualPrice !== '')
      ? subscriptionType === 'monthly'
        ? plan.monthlyPrice
        : plan.annualPrice
      : '—'
  }

  const featureRows = supportedFeatures.map((feature) => {
    if (feature.key === 'numAdmins') {
      return {
        ...feature,
        _free_tier: String(getAdminsCountByPlanKey('_free_tier') ?? 1),
        tier1: String(getAdminsCountByPlanKey('tier1') ?? 1),
        tier2: String(getAdminsCountByPlanKey('tier2') ?? 3),
        tier3:
          getAdminsCountByPlanKey('tier3') == null
            ? `${t('unlimited')}`
            : String(getAdminsCountByPlanKey('tier3')),
        tier4:
          getAdminsCountByPlanKey('tier4') == null
            ? `${t('unlimited')}`
            : String(getAdminsCountByPlanKey('tier4'))
      }
    }

    if (feature.key === 'eventPhotoAttachments') {
      return {
        ...feature,
        _free_tier: String(getPhotoQuotaLimitByPlanKey('_free_tier') ?? 25),
        tier1: String(getPhotoQuotaLimitByPlanKey('tier1') ?? 150),
        tier2: String(getPhotoQuotaLimitByPlanKey('tier2') ?? 500),
        tier3: String(getPhotoQuotaLimitByPlanKey('tier3') ?? 1500),
        tier4:
          getPhotoQuotaLimitByPlanKey('tier4') == null
            ? `${t('unlimited')}`
            : String(getPhotoQuotaLimitByPlanKey('tier4'))
      }
    }

    return feature
  })

  const freePlan = plans.find((plan) => plan.key === '_free_tier')
  const paidPlans = plans.filter((plan) => plan.key !== '_free_tier')
  const freeChecklist = ['numCalendars', 'eventPhotoAttachments', 'numAdmins']
    .map((key) => featureRows.find((feat) => feat.key === key))
    .filter((feat): feat is (typeof featureRows)[number] => Boolean(feat))

  return (
    <ConfirmationDialog
      title={''}
      fullWidth={true}
      classes={{
        paper: 'plan-container'
      }}
      noPaddingBottom
      noButtons
      scroll="body"
      disableScrollLock
      showCloseIcon
      content={
        <div className="membership-block" id="membership-block">
          <div className="subscription-header">
            <GenericDialog
              title=""
              message={activeMessage}
              leftButton={`${t('ok')}`}
              showing={infoDialogShowing}
              setShowing={handleActiveMessageClose}
            />

            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isProcessing}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            <DialogTitle />
          </div>

          {freePlan && (
            <div className="free-plan-box">
              <div className="free-plan-box__info">
                <div className="free-plan-box__tags">
                  <PlanNotice plan={freePlan} />
                  <span className="free-plan-box__badge">
                    {t('freePlanBadge', {
                      appName: isGroupcalApp() ? t('appName') : t('appName24me')
                    })}
                  </span>
                </div>
                <h3 className="free-plan-box__title">
                  {t('freePlanQuestion')}
                </h3>
              </div>
              <div className="free-plan-box__meta">
                <ul className="free-plan-box__checklist">
                  <li>
                    <CheckmarkIcon color="var(--site-brand)" />
                    <span>{t('freePlanUnlimitedCalendarsEvents')}</span>
                  </li>
                  {freeChecklist
                    .filter((feat) => feat.key !== 'numCalendars')
                    .map((feat) => (
                      <li key={feat.key}>
                        <CheckmarkIcon color="var(--site-brand)" />
                        <span>
                          {typeof feat._free_tier === 'boolean'
                            ? feat.title
                            : feat.key === 'numAdmins'
                              ? t('freePlanAdminPerCalendar')
                              : `${feat._free_tier} ${feat.title}`}
                        </span>
                      </li>
                    ))}
                </ul>
                <div className="free-plan-box__price">
                  <span
                    className="free-plan-box__price-value"
                    key={`${freePlan.key}-${subscriptionType}`}
                  >
                    {getDisplayedPrice(freePlan)}
                  </span>
                  <span className="free-plan-box__price-caption">
                    {t('forever')}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="plans-section-header">
            <span className="plans-section-header__tag">
              {t('paidPlansLabel')}
            </span>
            <div
              className="subscription-type"
              style={{
                opacity: pricesLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
            >
              <BillingToggle
                monthlyLabel={t('monthly')}
                yearlyLabel={yearlyPeriodLabel.trim()}
                savingsLabel={savingsLabel?.trim() ?? ''}
                value={subscriptionType}
                onChange={setSubscriptionType}
              />
            </div>
          </div>

          <div className="plan-cards-grid" ref={planCardsGridRef}>
            {paidPlans.map((plan) => {
              const planIndex = PLAN_ORDER.indexOf(plan.key)
              const previousPlanKey = PLAN_ORDER[planIndex - 1]
              const previousPlan = plans.find((p) => p.key === previousPlanKey)
              const planFeatures = PLAN_CARD_FEATURES[plan.key] ?? []
              const isActivePlanCard = Boolean(
                activePlan?.ID?.includes(plan.key)
              )
              const isRequiredPlanCard = Boolean(
                recommendedPlan?.includes(plan.key) &&
                  !activePlan?.ID?.includes(recommendedPlan)
              )
              const eyebrowKey = PLAN_EYEBROW_KEY[plan.key]

              return (
                <div
                  key={plan.key}
                  className={`plan-card plan-card--${plan.key}${
                    isRequiredPlanCard ? ' plan-card--required' : ''
                  }${isActivePlanCard ? ' plan-card--active' : ''}`}
                >
                  <PlanNotice plan={plan} />

                  <div className="plan-card__header">
                    {eyebrowKey && (
                      <div className="plan-card__eyebrow">
                        <span className="plan-card__eyebrow-dot" />
                        {t(eyebrowKey)}
                      </div>
                    )}
                    <h3 className="plan-card__name">{plan.name}</h3>
                    <p className="plan-card__desc">{plan.description}</p>
                  </div>

                  <div className="plan-card__price">
                    {plan.key === 'tier4' ? (
                      <>
                        <span
                          className="plan-card__price-value plan-price-animated plan-talk-with-us"
                          style={{ fontSize: '32px !important' }}
                        >
                          {t('enterpriseTalkWithUs')}
                        </span>
                        <p className="plan-card__price-caption">
                          {t('enterpriseCustomPricing')}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="plan-card__price-row">
                          <span
                            className="plan-card__price-value plan-price-animated"
                            key={`${plan.key}-${subscriptionType}`}
                          >
                            {getDisplayedPrice(plan)}
                          </span>
                          <span className="plan-card__price-unit">
                            / {t('month_one')}
                          </span>
                        </div>
                        {pricesLoaded &&
                          subscriptionType === 'annually' &&
                          plan.annualTotalPrice && (
                            <p className="plan-card__price-caption">
                              {t('billedAnnuallyAt', {
                                price: plan.annualTotalPrice
                              })}
                            </p>
                          )}
                        {pricesLoaded && subscriptionType === 'monthly' && (
                          <p className="plan-card__price-caption">
                            {t('billedMonthly')}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <PlanActionBtn plan={plan} />
                  {planFeatures.length > 0 && previousPlan && (
                    <>
                      <div className="plan-card__everything">
                        {t('everythingInPlanPlus', {
                          planName:
                            previousPlan.key === '_free_tier'
                              ? t('freePlanName')
                              : previousPlan.name
                        })}
                      </div>
                      <ul className="plan-card__feature-list">
                        {planFeatures.map((feature) => {
                          const featureRow = featureRows.find(
                            (row) => row.key === feature.featureKey
                          )
                          const value = feature.valueKey
                            ? featureRows.find(
                                (row) => row.key === feature.valueKey
                              )?.[plan.key]
                            : undefined
                          const formattedValue =
                            typeof value === 'string' &&
                            !Number.isNaN(Number(value))
                              ? Number(value).toLocaleString()
                              : value

                          return (
                            <li key={feature.translationKey}>
                              <CheckmarkIcon color="var(--tier-accent)" />
                              <span className="plan-card__feature-label">
                                {t(feature.translationKey, {
                                  value: formattedValue
                                })}
                              </span>
                              {featureRow && (
                                <PricingTooltip
                                  title={featureRow.desc}
                                  link={featureRow.linkUrl}
                                  linkText={featureRow.linkText}
                                  image={featureRow.imageUrl}
                                >
                                  <InfoOutlined
                                    className="plan-card__feature-info"
                                    sx={{ fontSize: '1rem' }}
                                  />
                                </PricingTooltip>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    </>
                  )}
                </div>
              )
            })}
          </div>

          <p className="plans-trial-note">{t('plansTrialNote')}</p>

          <div className="compare-section">
            <div className="compare-section__header">
              <span className="compare-section__tag">
                {t('fullFeatureMatrixLabel')}
              </span>
              <h2 className="compare-section__title">
                {t('fullFeatureComparisonTitle')}
              </h2>
              <p className="compare-section__desc">
                {t('fullFeatureComparisonDesc')}
              </p>
            </div>

            <div className="membership-table">
              <div
                aria-hidden="true"
                className={`plans-header-shadow${
                  isPlansHeaderSticky ? ' plans-header-shadow--visible' : ''
                }`}
                style={{ top: plansHeaderHeight }}
              />
              <Table
                className="plans-table"
                stickyHeader
                sx={{
                  minWidth: 1000,
                  width: '100%',
                  tableLayout: 'fixed',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  marginBottom: '16px'
                }}
              >
                <TableHead
                  className={`plans-header${
                    isPlansHeaderSticky ? ' plans-header--sticky' : ''
                  }`}
                  ref={plansHeaderRef}
                >
                  <TableRow>
                    <TableCell className="feature-col header-cell">
                      {t('featuresColumnLabel')}
                    </TableCell>
                    {plans.map((plan) => (
                      <TableCell
                        key={plan.key}
                        className={`plan-col header-cell ${plan.key}${
                          activePlan?.ID?.includes(plan.key) ||
                          (recommendedPlan?.includes(plan.key) &&
                            !activePlan?.ID?.includes(recommendedPlan))
                            ? ' plan-col--selected'
                            : ''
                        }`}
                      >
                        <div className="plan-header-mini">
                          <div
                            className="plan-header-mini__notice-slot"
                            style={{ height: noticeSlotHeight }}
                          >
                            <PlanNotice plan={plan} />
                          </div>
                          <span className="plan-header-mini__name">
                            {plan.name}
                          </span>
                          <span className="plan-header-mini__price">
                            {plan.key === 'tier4' ? (
                              t('enterpriseTalkWithUs')
                            ) : (
                              <>
                                <span
                                  className="plan-price-animated"
                                  key={`${plan.key}-${subscriptionType}`}
                                >
                                  {getDisplayedPrice(plan)}
                                </span>
                                {t('perMonthAbbrev')}
                              </>
                            )}
                          </span>
                          <PlanActionBtn plan={plan} inTable />
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {featureRows.map((feat, index) => {
                    const isNewCategory =
                      feat.category !== featureRows[index - 1]?.category
                    return (
                      <Fragment key={feat.key}>
                        {isNewCategory && (
                          <TableRow className="category-row">
                            <TableCell
                              colSpan={plans.length + 1}
                              className="category-cell"
                            >
                              {feat.category}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow
                          className={
                            openedForTierFeatures?.includes(
                              feat.key as TierFeatures
                            )
                              ? 'feature-row-highlighted'
                              : undefined
                          }
                          sx={{
                            backgroundColor: openedForTierFeatures?.includes(
                              feat.key as TierFeatures
                            )
                              ? hexToRgba(highlightAccent, 0.2)
                              : undefined
                          }}
                        >
                          <FeatureTitle
                            feature={feat}
                            highlight={openedForTierFeatures?.includes(
                              feat.key as TierFeatures
                            )}
                          />
                          {plans.map((plan) => {
                            const value = feat[plan.key]
                            return (
                              <TableCell
                                key={plan.key}
                                className={`plan-col ${plan.key}${
                                  activePlan?.ID?.includes(plan.key) ||
                                  (recommendedPlan?.includes(plan.key) &&
                                    !activePlan?.ID?.includes(recommendedPlan))
                                    ? ' plan-col--selected'
                                    : ''
                                }`}
                                align="center"
                              >
                                {typeof value === 'boolean' ? (
                                  value ? (
                                    <CheckmarkIcon color="var(--tier-accent)" />
                                  ) : (
                                    <span className="plan-col__dash">—</span>
                                  )
                                ) : (
                                  value
                                )}
                              </TableCell>
                            )
                          })}
                        </TableRow>
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            <Box height="32px" />
          </div>
        </div>
      }
      open={open}
      rightButtonHidden
      leftButtonHidden
      handleClose={handleDialogClose}
      handleLeftButton={handleDialogClose}
    />
  )
}

interface FeatureTitleProps {
  feature: (typeof supportedFeatures)[number]
  highlight?: boolean
}

function FeatureTitle(props: FeatureTitleProps) {
  const theme = useTheme()

  return (
    <TableCell
      className="feature-col"
      sx={{
        color: props.highlight ? theme.palette.text.primary : undefined,
        backgroundColor: props.highlight
          ? undefined
          : theme.palette.mode === 'dark'
            ? 'var(--pricing-surface)'
            : theme.palette.background.default
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <span>{props.feature.title}</span>
        <PricingTooltip
          title={props.feature.desc}
          link={props.feature.linkUrl}
          linkText={props.feature.linkText}
          image={props.feature.imageUrl}
        >
          <InfoOutlined
            id={`info-${props.feature.key}`}
            sx={{ fontSize: '1.3rem' }}
          />
        </PricingTooltip>
      </Box>
    </TableCell>
  )
}

export function subscriptionIdToPlan(id: number | string): PlanName {
  switch (id) {
    case PADDLE_SKU_PRO_MONTHLY:
    case PADDLE_SKU_PRO_YEARLY:
    case P_SKU_PRO_MONTHLY:
    case P_SKU_PRO_MONTHLY_NO_TRIAL:
    case P_SKU_PRO_YEARLY:
      return 'tier1'
    case PADDLE_SKU_BUSINESS_MONTHLY:
    case PADDLE_SKU_BUSINESS_YEARLY:
    case P_SKU_BUSINESS_MONTHLY:
    case P_SKU_BUSINESS_YEARLY:
      return 'tier2'
    case P_SKU_BUSINESS_PLUS_MONTHLY:
    case P_SKU_BUSINESS_PLUS_YEARLY:
    case PADDLE_SKU_BUSINESS_PLUS_MONTHLY:
    case PADDLE_SKU_BUSINESS_PLUS_YEARLY:
      return 'tier3'
    default:
      return '_free_tier'
  }
}

export function recommendedPlanToAnalyticsTier(
  recommendedPlan: PlanName | undefined | null
): string {
  if (recommendedPlan) {
    recommendedPlan
    switch (recommendedPlan) {
      case 'tier1':
        return 'Tier10'
      case 'tier2':
        return 'Tier20'
      case 'tier3':
        return 'Tier30'
      case 'tier4':
        return 'Tier40'
      default:
        return 'Free'
    }
  }

  return ''
}

export interface GTagProps {
  send_to: string
  value: string
  currency: string
  transaction_id: string
  event_callback?: () => void
}

export function logGTag(type: string, props: GTagProps) {
  if (!isTestEnv()) {
    gtag('event', type, props)
  }
}

function tierToLevel(tier: string): number {
  switch (tier) {
    case '_free_tier':
      return 0
    case 'tier1':
      return 1
    case 'tier2':
      return 2
    case 'tier3':
      return 3
    case 'tier4':
      return 4
    default:
      return 0
  }
}
